import { useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import {
    Button, Container, Form, FormField, FormInput, Header, Message, Segment, Step,
} from 'semantic-ui-react';

import { ResponsiveContainer } from '../../ResponsiveContainer';
import { PATHS } from '../../../paths';
import { useCanCreatePersonLoading } from '../../../hooks/canCreatePerson';
import { PersonIdPicker } from '../PersonIdPicker';
import { GiveLogin, GiveLoginOutcome, type GiveLoginResult } from '../../GiveLogin';

const GATE = gql`
  query CanCreateLogins {
    canCreateLogins
  }
`;

const CREATE_PERSON = gql`
  mutation AdminCreateUser(
    $name: String!, $preferredName: String, $picturePermission: Boolean!,
    $identifier: String, $tagIds: [Int!], $phone: String
  ) {
    adminCreateUser(
      name: $name, preferredName: $preferredName, picturePermission: $picturePermission,
      identifier: $identifier, tagIds: $tagIds, phone: $phone
    ) { id name }
  }
`;

/**
 * Adding a teacher who can sign in.
 *
 * ---------------------------------------------------------------------------------------
 * WHY TWO MUTATIONS AND NOT ONE
 * ---------------------------------------------------------------------------------------
 *
 * The person is created first (`adminCreateUser`), then given a login (`grantLogin`). The
 * backend deliberately kept these apart — `SupporterInvite.ts` explains why at length: a single
 * mutation taking a name and a credential has **no recovery from a typo**, and the thing it
 * leaves behind is unreachable through any UI.
 *
 * Split, this page is resumable in the way that matters. If the username is taken, or the email
 * was mistyped, or the request simply failed, the person exists and step two can be retried —
 * here, or later from their profile, which runs the identical component. Nothing has to be
 * undone first, and nothing is stranded.
 *
 * That is also why step one does **not** collect the email address: it belongs to the
 * credential, not to the person, and collecting it early would mean a failed step two had
 * already written it somewhere.
 */

const TeacherCreator = () => {
    const navigate = useNavigate();
    const { data: gate, loading: gateLoading } = useQuery(GATE);
    const [canCreatePerson, personGateLoading] = useCanCreatePersonLoading();

    const [name, setName] = useState('');
    const [preferredName, setPreferredName] = useState('');
    const [phone, setPhone] = useState('');
    const [identifier, setIdentifier] = useState('');
    const [tagIds, setTagIds] = useState<number[]>([]);
    const [showErrors, setShowErrors] = useState(false);
    const [error, setError] = useState('');

    /** Set the moment the person exists. From here, step one must never run again. */
    const [person, setPerson] = useState<{ id: number; name: string } | null>(null);
    const [result, setResult] = useState<GiveLoginResult | null>(null);

    const [createPerson, { loading: creating }] = useMutation(CREATE_PERSON);

    if (gateLoading || personGateLoading) {
        return <ResponsiveContainer><Container><p>Loading…</p></Container></ResponsiveContainer>;
    }

    // **Both gates, because this page makes two different calls.** Step one is `adminCreateUser`
    // (`CREATE_PERSON`, which CENTER_MANAGER also holds) and step two is `grantLogin`
    // (`CREATE_LOGIN`, which only ADMIN holds). They are deliberately different authorities, so
    // somebody can hold one without the other — and this is the one page that needs both.
    //
    // Asking only about the login half was wrong in the way that matters most here: the page
    // would have rendered for a centre manager, let them fill step one in, and *then* been
    // refused by `adminCreateUser`. Asking only about the person half would be worse still —
    // the person would exist and the credential would not.
    if (!gate?.canCreateLogins || !canCreatePerson) {
        return <Navigate to={PATHS.PEOPLE} replace />;
    }

    const complete = name.trim() !== '' && identifier !== '';

    const handleCreate = async () => {
        if (!complete) {
            setShowErrors(true);
            return;
        }

        setError('');

        try {
            const res = await createPerson({
                variables: {
                    name: name.trim(),
                    preferredName: preferredName.trim(),
                    phone: phone.trim(),
                    identifier,
                    tagIds,
                    // Not asked for here. A photo permission is a consent decision made with the
                    // person in front of you, and this form is often filled in without them;
                    // the profile is where it is set.
                    picturePermission: false,
                },
            });

            setPerson(res.data.adminCreateUser);
        } catch (e: any) {
            setError(e?.message || 'Could not create that person.');
        }
    };

    const step = result != null ? 3 : person != null ? 2 : 1;

    return (
        <ResponsiveContainer>
            <Segment style={{ padding: '1em 0em' }} vertical>
                <Container style={{ maxWidth: '700px' }}>
                    <Header as="h1">
                        Add a teacher
                        <Header.Subheader>
                            Creates the person and gives them a way to sign in.
                        </Header.Subheader>
                    </Header>

                    <Step.Group size="mini" fluid>
                        <Step active={step === 1} completed={step > 1}>
                            <Step.Content><Step.Title>Who</Step.Title></Step.Content>
                        </Step>
                        <Step active={step === 2} completed={step > 2}>
                            <Step.Content><Step.Title>Sign-in</Step.Title></Step.Content>
                        </Step>
                        <Step active={step === 3}>
                            <Step.Content><Step.Title>Hand over</Step.Title></Step.Content>
                        </Step>
                    </Step.Group>

                    {/* ── Step three: the result ──────────────────────────────────────── */}
                    {result != null && person != null && (
                        <Segment>
                            <GiveLoginOutcome
                                result={result}
                                name={person.name}
                                // Closing goes to their profile rather than back here: the next
                                // thing anyone does is put them on a class, and the profile is
                                // where the rest of their record is.
                                onClose={() => navigate(`/people/${person.id}`)}
                            />
                            <p style={{ color: '#767676', fontSize: '0.9em', marginTop: '0.8em' }}>
                                They can now be picked as a teacher when you{' '}
                                <Link to={PATHS.CLASS_CREATE}>create a class</Link>.
                            </p>
                        </Segment>
                    )}

                    {/* ── Step two: the login ─────────────────────────────────────────── */}
                    {result == null && person != null && (
                        <Segment>
                            <Message positive size="tiny">
                                <strong>{person.name}</strong> has been added. Now give them a way
                                to sign in.
                            </Message>
                            <GiveLogin
                                userId={person.id}
                                name={person.name}
                                onDone={setResult}
                            />
                            {/* The escape hatch that makes the split worth having: the person
                                exists either way, and step two is the same component on their
                                profile. Leaving here loses nothing. */}
                            <p style={{ color: '#767676', fontSize: '0.9em', marginTop: '1em' }}>
                                You can also do this later from{' '}
                                <Link to={`/people/${person.id}`}>their profile</Link> — they have
                                been added either way.
                            </p>
                        </Segment>
                    )}

                    {/* ── Step one: the person ────────────────────────────────────────── */}
                    {person == null && (
                        <Segment>
                            <Form error={!!error} size="small">
                                <FormField>
                                    <label>Identifier</label>
                                    <PersonIdPicker
                                        setId={setIdentifier}
                                        setTagIds={setTagIds}
                                        showErrors={showErrors}
                                    />
                                    <FormInput
                                        fluid
                                        readOnly
                                        value={identifier}
                                        placeholder="(Automatically allocated)"
                                    />
                                </FormField>

                                <Form.Group widths="equal">
                                    <FormInput
                                        fluid
                                        label="Name"
                                        placeholder="Name"
                                        value={name}
                                        error={showErrors && name.trim() === '' && 'Please enter a name'}
                                        onChange={(e) => setName(e.target.value)}
                                    />
                                    <FormInput
                                        fluid
                                        label="Preferred name"
                                        placeholder="Preferred name"
                                        value={preferredName}
                                        onChange={(e) => setPreferredName(e.target.value)}
                                    />
                                </Form.Group>

                                <FormInput
                                    fluid
                                    label="Phone"
                                    placeholder="Phone"
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                />

                                {error && <Message error content={error} />}

                                <Button
                                    primary
                                    loading={creating}
                                    disabled={creating}
                                    onClick={handleCreate}
                                >
                                    Next
                                </Button>
                                <Button basic as={Link} to={PATHS.PEOPLE} disabled={creating}>
                                    Cancel
                                </Button>
                            </Form>

                            <p style={{ color: '#767676', fontSize: '0.9em', marginTop: '1em' }}>
                                Already in Joyya and just needs a login? Find them on{' '}
                                <Link to={PATHS.PEOPLE}>People</Link> and use <em>Give login</em> on
                                their profile.
                            </p>
                        </Segment>
                    )}
                </Container>
            </Segment>
        </ResponsiveContainer>
    );
};

export { TeacherCreator };
